import {
  Task,
  defModule,
  type TaskDeclaration,
  type TaskMessage,
  type UpdateResult
} from '@st/redux'
import type { Organization } from '@st/sdk'
import { Effect } from 'effect'
import { match, P } from '@st/pattern'
import {
  Analytics,
  ErrorReporting,
  retryOrganizationContext,
  type OrganizationContextState
} from '~/effects'
import { observeOrganizationContext } from '~/features/module-runtime'

type OrganizationContext = {
  currentUserId: string | null
  organizationSlug: string | null
  organization: undefined
}

export type OrganizationState =
  | (OrganizationContext & { status: 'idle' })
  | (Omit<OrganizationContext, 'organizationSlug'> & {
      status: 'loading'
      organizationSlug: string
    })
  | (Omit<OrganizationContext, 'organizationSlug'> & {
      status: 'loadFailed'
      organizationSlug: string
    })
  | (Omit<OrganizationContext, 'organizationSlug'> & {
      status: 'unavailable'
      organizationSlug: string
    })
  | (Omit<OrganizationContext, 'organizationSlug' | 'organization'> & {
      status: 'loaded'
      organizationSlug: string
      organization: Organization
    })

const observeActiveOrganization = observeOrganizationContext(
  (context) => ({ type: 'organizationContextChanged', context }) as const
)

const publishOrganizationContext = Task.define(
  (organization: Organization) =>
    Effect.gen(function* () {
      const analytics = yield* Analytics
      const errorReporting = yield* ErrorReporting
      yield* analytics.setOrganization(organization)
      yield* errorReporting.setTags({ organization_id: organization.id })
      return null
    }),
  { key: (organization) => organization.id }
)

const clearOrganizationErrorContext = Task.define(() =>
  Effect.gen(function* () {
    const errorReporting = yield* ErrorReporting
    yield* errorReporting.setTags({ organization_id: undefined })
    return null
  })
)

export type OrganizationMessage =
  | TaskMessage<typeof observeActiveOrganization>
  | { type: 'retryOrganizationRequested' }

/**
 * This process is the serializable application projection of the canonical
 * organization context. Resolution and cancellation live at that shared
 * Effect boundary; this module owns only app state and observability effects.
 */
export const organizationModule = defModule<{
  state: OrganizationState
  message: OrganizationMessage
}>({
  init: () => ({
    status: 'idle',
    currentUserId: null,
    organizationSlug: null,
    organization: undefined
  }),
  tasks: organizationTasks,
  update: updateOrganization
})

function organizationTasks(state: OrganizationState): TaskDeclaration<OrganizationMessage>[] {
  const observations: TaskDeclaration<OrganizationMessage>[] = [observeActiveOrganization()]

  return match(state)
    .returnType<TaskDeclaration<OrganizationMessage>[]>()
    .with({ status: 'loading' }, () => observations)
    .with({ status: 'loaded' }, ({ organization }) => [
      ...observations,
      publishOrganizationContext(organization)
    ])
    .with({ status: 'loadFailed' }, () => [...observations, clearOrganizationErrorContext()])
    .with({ status: 'unavailable' }, () => [...observations, clearOrganizationErrorContext()])
    .with({ status: 'idle' }, () => observations)
    .exhaustive()
}

function updateOrganization(
  state: OrganizationState,
  message: OrganizationMessage
): UpdateResult<OrganizationState, OrganizationMessage> {
  return match({ state, message })
    .returnType<UpdateResult<OrganizationState, OrganizationMessage>>()
    .with({ message: { type: 'organizationContextChanged' } }, ({ message }) =>
      toOrganizationState(message.context)
    )
    .with(
      { state: { status: 'loadFailed' }, message: { type: 'retryOrganizationRequested' } },
      ({ state }) => [state, retryOrganizationContext.pipe(Effect.as(null))]
    )
    .otherwise(({ state, message }) => {
      throw new Error(`Unexpected organization message ${message.type} while ${state.status}`)
    })
}

function toOrganizationState(context: OrganizationContextState): OrganizationState {
  return match(context)
    .returnType<OrganizationState>()
    .with({ status: 'idle' }, (context) => ({ ...context, organization: undefined }))
    .with({ status: P.union('loading', 'loadFailed') }, (context) => ({
      ...context,
      organization: undefined
    }))
    .with({ status: 'unavailable' }, (context) => ({ ...context, organization: undefined }))
    .with({ status: 'loaded' }, (context) => context)
    .exhaustive()
}

export function selLoadedOrganizationId(state: OrganizationState): string | undefined {
  return state.status == 'loaded' ? state.organization.id : undefined
}
